const REVIEW_SELECTOR = '.jm-review-item, .jdgm-rev';
const GALLERY_SELECTORS = [
  '.jdgm-rev__pics',
  '.jm-review-content__media',
  '.jm-review-item__media',
  '.jm-review-media',
  '.jm-media-list',
  '[class*="review-media"]',
  '[class*="review_media"]',
];

const mediaSelector = 'img, video';

function mediaItemFor(element, gallery) {
  let item = element.closest('a, button, [role="button"]');
  if (!item || !gallery.contains(item)) item = element.parentElement;

  while (item?.parentElement && item.parentElement !== gallery && item.parentElement.querySelectorAll(mediaSelector).length === 1) {
    item = item.parentElement;
  }

  return item;
}

function commonAncestor(elements, boundary) {
  let candidate = elements[0]?.parentElement;
  while (candidate && candidate !== boundary && !elements.every((element) => candidate.contains(element))) {
    candidate = candidate.parentElement;
  }
  return candidate && candidate !== boundary ? candidate : null;
}

function findGallery(review) {
  const judgeMeThumbnails = [...review.querySelectorAll('.jm-media-thumbnail-list__thumbnail')];
  if (judgeMeThumbnails.length > 1) {
    const thumbnailGallery = commonAncestor(judgeMeThumbnails, review);
    if (thumbnailGallery) return thumbnailGallery;
  }

  for (const selector of GALLERY_SELECTORS) {
    for (const gallery of review.querySelectorAll(selector)) {
      if (gallery.querySelectorAll(mediaSelector).length > 1) return gallery;
    }
  }

  const media = [...review.querySelectorAll(mediaSelector)].filter((element) => {
    if (element.closest('.jm-reviewer-info, .jdgm-rev__author, [class*="avatar"], [class*="badge"]')) return false;
    if (element.tagName === 'VIDEO') return true;

    const image = /** @type {HTMLImageElement} */ (element);
    const description = `${image.alt} ${image.className} ${image.src}`.toLowerCase();
    return /review|media|photo|picture|pic|judgeme/.test(description);
  });

  return media.length > 1 ? commonAncestor(media, review) : null;
}

function arrowIcon(direction) {
  const path = direction === 'previous' ? 'M10 3 5 8l5 5' : 'm6 3 5 5-5 5';
  return `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="${path}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function enhanceGallery(gallery) {
  if (gallery.dataset.reviewCarouselReady === 'true') return;

  const media = [...gallery.querySelectorAll(mediaSelector)];
  if (media.length < 2) return;

  media.forEach((element) => {
    if (element instanceof HTMLImageElement) element.draggable = false;
  });

  const items = [...new Set(media.map((element) => mediaItemFor(element, gallery)).filter(Boolean))];
  if (items.length < 2) return;

  gallery.dataset.reviewCarouselReady = 'true';
  gallery.classList.add('review-media-carousel__track');
  items.forEach((item) => item.classList.add('review-media-carousel__item'));

  const wrapper = document.createElement('div');
  wrapper.className = 'review-media-carousel';
  gallery.before(wrapper);
  wrapper.append(gallery);

  const previous = document.createElement('button');
  previous.type = 'button';
  previous.className = 'review-media-carousel__button review-media-carousel__button--previous';
  previous.setAttribute('aria-label', 'Ảnh trước');
  previous.innerHTML = arrowIcon('previous');

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'review-media-carousel__button review-media-carousel__button--next';
  next.setAttribute('aria-label', 'Ảnh tiếp theo');
  next.innerHTML = arrowIcon('next');

  wrapper.append(previous, next);

  let activeIndex = 0;
  let currentTranslate = 0;
  let dragStartX = 0;
  let dragStartTranslate = 0;
  let isDragging = false;
  let suppressClick = false;

  const getMetrics = () => {
    const gap = Number.parseFloat(getComputedStyle(gallery).columnGap) || 0;
    const slideWidth = Math.min(160, Math.max(96, (wrapper.clientWidth - gap) / 2));
    wrapper.style.setProperty('--review-media-slide-width', `${slideWidth}px`);

    const step = slideWidth + gap;
    const trackWidth = items.length * slideWidth + Math.max(0, items.length - 1) * gap;
    return { step, maxTranslate: Math.max(0, trackWidth - wrapper.clientWidth) };
  };

  const render = () => {
    const { step, maxTranslate } = getMetrics();
    const target = Math.min(activeIndex * step, maxTranslate);
    currentTranslate = -target;
    gallery.style.setProperty('--review-media-translate', `${currentTranslate}px`);

    const hasOverflow = maxTranslate > 2;
    wrapper.classList.toggle('review-media-carousel--overflow', hasOverflow);
    previous.disabled = !hasOverflow || target <= 2;
    next.disabled = !hasOverflow || target >= maxTranslate - 2;
  };

  const move = (direction) => {
    const { step, maxTranslate } = getMetrics();
    const lastIndex = Math.ceil(maxTranslate / step);
    activeIndex = Math.min(lastIndex, Math.max(0, activeIndex + direction));
    render();
  };

  previous.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    move(-1);
  });
  next.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    move(1);
  });

  gallery.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    dragStartX = event.clientX;
    dragStartTranslate = currentTranslate;
    isDragging = true;
    suppressClick = false;
    wrapper.classList.add('review-media-carousel--dragging');
    gallery.setPointerCapture?.(event.pointerId);
  });
  gallery.addEventListener('dragstart', (event) => event.preventDefault());

  gallery.addEventListener('pointermove', (event) => {
    if (!isDragging) return;
    const distance = event.clientX - dragStartX;
    if (Math.abs(distance) > 4) suppressClick = true;

    const { maxTranslate } = getMetrics();
    currentTranslate = Math.min(0, Math.max(-maxTranslate, dragStartTranslate + distance));
    gallery.style.setProperty('--review-media-translate', `${currentTranslate}px`);
  });

  const finishDrag = (event) => {
    if (!isDragging) return;
    isDragging = false;
    wrapper.classList.remove('review-media-carousel--dragging');
    gallery.releasePointerCapture?.(event.pointerId);

    const distance = event.clientX - dragStartX;
    if (Math.abs(distance) >= 30) {
      move(distance < 0 ? 1 : -1);
    } else {
      render();
    }
  };

  gallery.addEventListener('pointerup', finishDrag);
  gallery.addEventListener('pointercancel', finishDrag);
  gallery.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClick = false;
    },
    true
  );

  new ResizeObserver(render).observe(wrapper);
  requestAnimationFrame(render);
}

function initReviewMediaCarousels(root = document) {
  root.querySelectorAll(REVIEW_SELECTOR).forEach((review) => {
    const gallery = findGallery(review);
    if (gallery) enhanceGallery(gallery);
  });
}

let refreshQueued = false;
const observer = new MutationObserver(() => {
  if (refreshQueued) return;
  refreshQueued = true;
  requestAnimationFrame(() => {
    refreshQueued = false;
    initReviewMediaCarousels();
  });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initReviewMediaCarousels(), { once: true });
} else {
  initReviewMediaCarousels();
}

observer.observe(document.documentElement, { childList: true, subtree: true });
